import { useState, useEffect, useRef, useCallback } from 'react';
import { apiFetch } from '../api/client.js';

const POLL_INTERVAL = 15000; // 15 seconds

/**
 * Notification bell icon with badge count and dropdown.
 * Polls /api/notifications every 15s.
 */
export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch('/notifications');
      setNotifications(data);
    } catch (_err) {
      // Silently fail on poll errors
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [load]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markRead = async (id) => {
    try {
      await apiFetch(`/notifications/${id}/read`, { method: 'PUT' });
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (_err) {
      // ignore
    }
  };

  const markAllRead = async () => {
    try {
      await apiFetch('/notifications/read-all', { method: 'PUT' });
      setNotifications([]);
      setOpen(false);
    } catch (_err) {
      // ignore
    }
  };

  const count = notifications.length;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-1 text-white hover:opacity-80"
        aria-label="Notifications"
      >
        <BellIcon />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-10 w-80 bg-white rounded-xl shadow-xl border z-[100] max-h-96 overflow-y-auto">
          <div className="flex justify-between items-center p-3 border-b">
            <span className="font-bold text-sm text-gray-800">Notifications</span>
            {count > 0 && (
              <button onClick={markAllRead} className="text-xs text-blue-600 hover:underline">
                Tout marquer lu
              </button>
            )}
          </div>
          {count === 0 ? (
            <p className="text-gray-400 text-sm text-center py-6">Aucune notification</p>
          ) : (
            notifications.map((n) => (
              <div
                key={n.id}
                onClick={() => markRead(n.id)}
                className="p-3 border-b last:border-0 hover:bg-gray-50 cursor-pointer"
              >
                <p className="text-sm text-gray-800">{n.message}</p>
                <span className="text-xs text-gray-400">
                  {new Date(n.created_at).toLocaleString('fr-FR')}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/** Simple bell SVG icon */
function BellIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  );
}
